import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { claimsInvoiceActions } from '../../../../store';
import { FORTA_BLUE } from '../../../../utils/colors';
import { FHButton, FHConfirmDialog, FHSimpleTable } from '../../../base';
import {
  getInvoiceTableTheme,
  getInvoiceTableColumns,
  getInvoiceClaimDetailsTableOptions,
  getInvoiceReadOnlyPaymentTableColumns,
  getInvoiceReadOnlyPaymentsOptions,
} from './config';
import { calculateClaimsAndPaymentTotal, transformInvoiceClaimDetailsTableData } from './utils';
import { getUtcOffset, formatPhoneNum } from '../../../../utils/common';
import { convertDateTimeToCTTime, history, usDollarFormatter } from '../../../../helpers';
import { DATE_DISPLAY_FORMAT, Themes } from '../../../../constants';
import { InvoicePaymentStatusEnum } from '../claim-info/claim-payments/constants';

import type { IInvoiceDetails } from '../../../../utils/types';

interface IInvoicePreviewProps {
  selectedPayee?: number | null;
  previewMode?: boolean;
  invoiceData: IInvoiceDetails | null;
  handlePDFDownload?: () => void;
}

function InvoicePreview({
  previewMode = false,
  invoiceData,
  selectedPayee,
  handlePDFDownload,
}: IInvoicePreviewProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('invoice');
  const { getAccessTokenSilently } = useAuth0();
  const [claimTotals, paymentTotal] = useMemo(
    () => calculateClaimsAndPaymentTotal(invoiceData),
    [invoiceData],
  );

  const [openCreateInvoiceModal, setOpenCreateInvoiceModal] = useState<boolean>(false);

  const handleCreateInvoice = async () => {
    if (!invoiceData || !selectedPayee) return;
    const { client, claims, date_due, discount } = invoiceData;
    const data = {
      client_id: client?.id,
      claim_ids: claims?.map((claim) => claim.claim_id),
      date_due,
      discount: discount ?? null,
      payee_provider_id: selectedPayee,
      date_generated: moment(new Date()),
      utc_offset_date_generated: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      claimsInvoiceActions.createInvoice({ getAccessTokenSilently, data }),
    );
    setOpenCreateInvoiceModal(false);
    if (!response.error) {
      history.navigate(`/billings/invoice/${response.payload?.id}`);
    }
  };

  const fillteredInvoicePayments =
    invoiceData?.payments?.filter(
      (payment) => payment.status === InvoicePaymentStatusEnum.SUCCESS,
    ) ?? [];

  if (previewMode && !invoiceData) return <Typography>Preview not available</Typography>;
  return (
    <Box>
      <Grid container textAlign="left" gap={2}>
        <Grid item sm={4} xs={6} md={2.25}>
          <Box>
            <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
              Bill to
            </Typography>
            <Typography
              fontWeight={700}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              }}
            >
              {invoiceData?.payee?.first_name} {invoiceData?.payee?.last_name}
            </Typography>
            {invoiceData?.payee?.street_address_line_one && (
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.payee?.street_address_line_one}
              </Typography>
            )}
            {invoiceData?.payee?.street_address_line_two && (
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.payee?.street_address_line_two}
              </Typography>
            )}
            <Box>
              <Typography
                display="inline"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.payee?.street_address_city ?? ''}
              </Typography>
              <Typography display="inline">
                {`, ${invoiceData?.payee?.street_address_state ?? ''} `}
              </Typography>
              <Typography display="inline">
                {invoiceData?.payee?.street_address_zip ?? ''}
              </Typography>
            </Box>
            {invoiceData?.payee?.phone && (
              <Typography>{formatPhoneNum(invoiceData?.payee?.phone)}</Typography>
            )}
          </Box>
        </Grid>
        <Grid item sm={4} xs={6} md={2}>
          <Box>
            <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
              Client
            </Typography>
            <Typography
              fontWeight={700}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              }}
            >
              {invoiceData?.client?.first_name} {invoiceData?.client?.last_name}
            </Typography>
            {invoiceData?.client?.street_address_line_one && (
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.client?.street_address_line_one}
              </Typography>
            )}
            {invoiceData?.client?.street_address_line_two && (
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.client?.street_address_line_two}
              </Typography>
            )}
            <Box>
              <Typography
                display="inline"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              >
                {invoiceData?.client?.street_address_city ?? ''}
              </Typography>
              <Typography display="inline">
                {`, ${invoiceData?.client?.street_address_state ?? ''} `}
              </Typography>
              <Typography display="inline">
                {invoiceData?.client?.street_address_zip ?? ''}
              </Typography>
            </Box>
            {invoiceData?.client?.phone && (
              <Typography>{formatPhoneNum(invoiceData?.client?.phone)}</Typography>
            )}
          </Box>
        </Grid>
        <Grid item sm={4} xs={6} md={1.5} mr={2}>
          <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1}>
            Billing Provider
          </Typography>
          <Typography>{invoiceData?.billing_facility}</Typography>
        </Grid>
        <Grid item sm={4} xs={6} md={1.5}>
          <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1}>
            Invoice date
          </Typography>
          {invoiceData?.date_generated && (
            <Typography>
              {convertDateTimeToCTTime(invoiceData?.date_generated).format(DATE_DISPLAY_FORMAT)}
            </Typography>
          )}
        </Grid>
        <Grid item sm={4} xs={6} md={1.5}>
          <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1}>
            Due date
          </Typography>
          <Typography>{moment(invoiceData?.date_due).format('MM/DD/YY')}</Typography>
        </Grid>
        <Grid
          item
          sm={4}
          xs={6}
          md={2.25}
          display={'flex'}
          alignItems={'center'}
          flexDirection={'column'}
        >
          <Typography color={FORTA_BLUE.primary} fontSize={20} fontWeight={700} mb={2}>
            Amount due: {usDollarFormatter.format(invoiceData?.amount_due ?? 0)}
          </Typography>
          {!previewMode && (
            <FHButton
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handlePDFDownload}
            >
              Download PDF
            </FHButton>
          )}
        </Grid>
      </Grid>
      <Divider sx={{ mt: 4 }} />
      <Typography textAlign="left" fontWeight={700} mb={1} sx={{ mt: 3 }}>
        Services included in this invoice
      </Typography>
      <FHSimpleTable
        data={transformInvoiceClaimDetailsTableData(invoiceData?.claims ?? [])}
        columnDefinitions={getInvoiceTableColumns(t)}
        columnOptions={getInvoiceClaimDetailsTableOptions(
          claimTotals,
          invoiceData?.is_any_claim_paid_to_subscriber ?? false,
        )}
        tableTheme={getInvoiceTableTheme(Themes.Primary)}
      />
      {fillteredInvoicePayments.length > 0 && (
        <>
          <Typography textAlign="left" fontWeight={700} mb={1} mt={3}>
            Payments received for this invoice
          </Typography>
          <FHSimpleTable
            data={fillteredInvoicePayments}
            columnDefinitions={getInvoiceReadOnlyPaymentTableColumns(t)}
            columnOptions={getInvoiceReadOnlyPaymentsOptions(paymentTotal)}
            tableTheme={getInvoiceTableTheme(Themes.Primary)}
          />
        </>
      )}
      {previewMode && invoiceData && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 4 }}>
            <FHButton
              sx={{ minWidth: 200, p: 2, borderRadius: '8px' }}
              onClick={() => setOpenCreateInvoiceModal(true)}
              data-testid="createInvoiceBtn"
            >
              Create Invoice
            </FHButton>
          </Box>
          <FHConfirmDialog
            sx={{
              '& .MuiModal-backdrop': {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
              },
            }}
            titleStyles={{
              color: FORTA_BLUE.primary,
              fontWeight: 500,
            }}
            dialogStyles={{
              padding: '24px 16px',
              maxHeight: '85vh',
              height: 'auto',
              width: '650px',
              maxWidth: '650px',
            }}
            title="Create invoice"
            contentRenderer={() => (
              <Box
                sx={{
                  height: '150px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography fontSize={21} textAlign="center" p={5}>
                  {invoiceData?.discount !== null && invoiceData?.discount > 0
                    ? `Do you want to create the invoice with a discount of ${usDollarFormatter.format(
                        invoiceData.discount,
                      )}?`
                    : 'Do you want to create the invoice?'}
                </Typography>
              </Box>
            )}
            open={openCreateInvoiceModal}
            submittext="Continue"
            canceltext="Cancel"
            submitBtnTestId="submitConfirmDialog"
            cancelBtnTestId="cancelButton"
            onClose={() => setOpenCreateInvoiceModal(false)}
            onSubmit={handleCreateInvoice}
          />
        </>
      )}
    </Box>
  );
}

export default InvoicePreview;
